/**
 * Remove-small-parts proposal card (the runtime HITL gate). The removal
 * edits the structure IN PLACE, like the toolbar's Remove small parts
 * panel. Pure presentational: it shows the RESOLVED target, the clinician's
 * threshold in cm³, how many disconnected parts the preview removed out of
 * how many, the previewed volume change, and a note that the structure
 * itself changes (undo restores it); nothing mutates until the clinician
 * presses Apply. The card frame, status chip, hover preview, and
 * Apply/Reject gate live in {@link ProposalCardShell}.
 */
import { Typography } from '@mui/material';
import { formatCmTrimmed, formatSignedCm3 } from '~/utils/units';
import ProposalCardShell, {
    PreviewedVolumeLine,
    ProposalSummaryBox,
    TargetLine,
    type ProposalStatus,
} from './ProposalCardShell';

export interface RemoveSmallPartsProposalCardProps {
    readonly targetName: string;
    readonly targetRoiNumber: number;
    readonly currentVolumeCm3: number;
    readonly minVolumeCm3: number;
    readonly partsFound: number;
    readonly partsRemoved: number;
    readonly volumeDeltaCm3: number;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    /** Hover/focus the card to preview the proposed geometry in the viewport. */
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

export default function RemoveSmallPartsProposalCard({
    targetName,
    targetRoiNumber,
    currentVolumeCm3,
    minVolumeCm3,
    partsFound,
    partsRemoved,
    volumeDeltaCm3,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: RemoveSmallPartsProposalCardProps) {
    return (
        <ProposalCardShell
            title="Remove small parts"
            ariaLabel="Remove small parts proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <TargetLine
                targetName={targetName}
                targetRoiNumber={targetRoiNumber}
                currentVolumeCm3={currentVolumeCm3}
            />

            <ProposalSummaryBox>
                remove parts &lt; {formatCmTrimmed(minVolumeCm3)} cm³ from “
                {targetName}”
                <br />
                {partsRemoved} of {partsFound} parts removed
            </ProposalSummaryBox>

            <PreviewedVolumeLine
                label="Previewed volume change"
                value={formatSignedCm3(volumeDeltaCm3)}
            />

            <Typography variant="caption" color="text.secondary">
                This edits “{targetName}” itself, like the toolbar&apos;s Remove
                small parts; no new structure is created. Undo restores it.
            </Typography>
        </ProposalCardShell>
    );
}
